import React, { useRef, useEffect } from 'react'
import {
  Checkbox,
  Col,
  Dropdown,
  Form,
  Icon,
  Menu,
  notification,
  Row,
  Switch,
  Tooltip,
  Typography
} from 'antd'
import { ClickParam } from 'antd/lib/menu'
import { Formik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'

import { ApplicationState } from 'src/store'
import { Button } from 'src/components/Ant'
import { ColumnProps } from 'src/components/Ant/Table'
import { currencyMask } from 'src/components/Formik'
import { DatePicker, TextArea, InputCurrency } from 'src/components/Formik'
import { getAgreement } from 'src/core/store/ducks/partners/agreement/actions'
import { Id } from 'src/types'
import { sendAgreementOffer } from 'src/core/store/ducks/partners/agreementsOffer/actions'
import { sendBedroomsOffer } from 'src/core/store/ducks/partners/offer/actions'
import { useT } from 'src/core/hooks/use-translation'

import { initialValues } from './initial-values'
import * as CSS from './styles'
import validationSchema from './validation-schema'

import { DataSourceBedroomsOffer } from '../..'

interface Props {
  agency_partner_id: string
  dataSource: DataSourceBedroomsOffer[]
  roomId: Id
}

export function SideModalOffer({
  agency_partner_id,
  dataSource,
  roomId
}: Props) {
  const { agreement, me } = useSelector((state: ApplicationState) => ({
    agreement: state.agreement,
    me: state.me
  }))

  const { t } = useT()

  const dispatch = useDispatch()
  const refFormikOffer = useRef(null)

  useEffect(() => {
    dispatch(getAgreement({ agency_partner_id: me.user.organization_id }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function onDate(date: string, format: string) {
    return moment.unix(Number(date)).utc().format(format)
  }

  const columns: ColumnProps<unknown>[] = [
    {
      title: 'common.period',
      key: 'period',
      render: (record: any) => (
        <Typography.Text>{`${onDate(record.start_date, 'DD/MM/YYYY')} ${onDate(
          record.end_date,
          'DD/MM/YYYY'
        )}`}</Typography.Text>
      )
    },
    {
      dataIndex: 'fare_value',
      key: 'fare_value',
      render: (fare_value: number) => (
        <Typography.Text>{`BRL ${
          currencyMask({
            formatStr: true,
            value: fare_value
          }).maskedValue
        }`}</Typography.Text>
      ),
      title: 'common.value'
    },
    {
      dataIndex: 'tax_value',
      key: 'tax_value',
      render: (tax_value: number) => (
        <Typography.Text>{`BRL ${
          currencyMask({
            formatStr: true,
            value: tax_value
          }).maskedValue
        }`}</Typography.Text>
      ),
      title: 'common.rate'
    },
    {
      title: '',
      dataIndex: 'description',
      key: 'description',
      render: (description: string) => (
        <Tooltip title={description || ''}>
          <Icon type="copy" />
        </Tooltip>
      )
    },
    {
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean, record: any) => (
        <Switch
          checked={active}
          checkedChildren={t('web.resources.common.active')}
          onChange={checked => {
            if (
              checked &&
              typeof record.agency_partner_hotel_agreement
                ?.agency_partner_hotel_offer_id === 'string'
            ) {
              notification.success({
                duration: 10,
                message: t('web.table.head-partner.has-no-agreement-linked'),
                placement: 'bottomRight'
              })
              return
            }
            dispatch(
              sendBedroomsOffer([
                {
                  agency_partner_hotel_offer_id: record.id,
                  active: !active,
                  agency_partner_id
                }
              ])
            )
          }}
          unCheckedChildren={t('web.resources.common.inactive')}
        />
      )
    },
    {
      dataIndex: 'agency_partner_hotel_agreement',
      key: 'agency_partner_hotel_agreement',
      render: (agency_partner_hotel_agreement: any) => {
        if (
          !agency_partner_hotel_agreement?.active ||
          !agency_partner_hotel_agreement?.agency_partner_hotel_agreement_id ||
          !agency_partner_hotel_agreement?.agency_partner_hotel_offer_id ||
          !agency_partner_hotel_agreement?.agreement
        ) {
          return <></>
        }
        return (
          <Tooltip title={agency_partner_hotel_agreement?.agreement.name || ''}>
            <Icon type="link" />
          </Tooltip>
        )
      }
    },
    {
      key: 'actions',
      render: (record: any) => (
        <Dropdown
          overlay={
            <Menu
              onClick={({ key }: ClickParam) => {
                switch (key) {
                  case 'edit':
                    const start_date = moment(
                      onDate(record.start_date, 'YYYY-MM-DD')
                    ).unix()
                    const end_date = moment(
                      onDate(record.end_date, 'YYYY-MM-DD')
                    ).unix()

                    refFormikOffer.current?.resetForm({
                      ...record,
                      start_date,
                      end_date
                    })
                    break
                  default:
                    dispatch(
                      sendAgreementOffer([
                        {
                          agency_partner_id,
                          agency_partner_hotel_offer_id: record.id,
                          agreement_id: key,
                          description: record.description,
                          active: true
                        }
                      ])
                    )
                }
              }}
            >
              <Menu.Item key="edit">{t('common.actions.edit')}</Menu.Item>
              {(agreement.list.length > 0 &&
                agreement.list.map(item => {
                  const title = item.code
                    ? `${item.code} ${item.name}`
                    : item.name
                  return (
                    <Menu.Item key={item.agreement_id} title={title}>
                      {title}
                    </Menu.Item>
                  )
                })) || (
                <Menu.Item
                  key="link"
                  disabled
                  title={t('web.table.head-partner.has-no-agreement')}
                >
                  {t('web.table.head-partner.has-no-agreement')}
                </Menu.Item>
              )}
            </Menu>
          }
          placement="bottomRight"
        >
          <Button type="link">
            <Icon type="more" />
          </Button>
        </Dropdown>
      ),
      width: 50
    }
  ]

  function handleSubmit(values: typeof initialValues) {
    const startDate = onDate(values.start_date, 'YYYY-MM-DD')
    const endDate = onDate(values.end_date, 'YYYY-MM-DD')
    dispatch(
      sendBedroomsOffer([
        {
          ...values,
          start_date: startDate,
          end_date: endDate,
          tax_value: Number(values.tax_value),
          fare_value: Number(values.fare_value),
          agency_partner_id,
          partner_hotel_room_id: roomId
        }
      ])
    )
    refFormikOffer.current.resetForm(initialValues)
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        ref={refFormikOffer}
        render={formikProps => (
          <Form style={{ marginBottom: '3em' }}>
            <Row gutter={[12, 0]}>
              <Col span={12}>
                <DatePicker
                  formikProps={formikProps}
                  label="common.startDate"
                  name="start_date"
                  placeholder="web.receipt.form.date.placeholder"
                />
              </Col>
              <Col span={12}>
                <DatePicker
                  formikProps={formikProps}
                  label="common.endDate"
                  name="end_date"
                  placeholder="web.receipt.form.date.placeholder"
                />
              </Col>
            </Row>
            <Row gutter={[12, 0]}>
              <Col span={12}>
                <InputCurrency
                  dataTestId="fare-value"
                  formikProps={formikProps}
                  label="common.value"
                  name="fare_value"
                  required
                />
              </Col>
              <Col span={12}>
                <InputCurrency
                  dataTestId="tax-value"
                  formikProps={formikProps}
                  label="common.rate"
                  name="tax_value"
                  required
                />
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <TextArea
                  formikProps={formikProps}
                  label="web.table.head-partner.description.name"
                  name="description"
                  placeholder="web.table.head-partner.description.placeholder"
                  required
                  rows={4}
                />
              </Col>
            </Row>
            <Row>
              <Col
                span={20}
                onClick={() => {
                  formikProps.setFieldValue(
                    'pension',
                    !formikProps.values.pension
                  )
                }}
              >
                <Typography.Text>
                  {t('web.table.bedrooms.pension')}
                  {': '}
                </Typography.Text>
                <Checkbox
                  checked={formikProps.values.pension}
                  data-test-id="bedroom-pension"
                />
              </Col>
              <Col span={4}>
                <CSS.Button
                  htmlType="submit"
                  title="common.actions.save"
                  type="primary"
                  onClick={formikProps.submitForm}
                />
              </Col>
            </Row>
          </Form>
        )}
        validationSchema={validationSchema}
      />
      <CSS.Table
        className="table-striped"
        columns={columns}
        dataSource={dataSource}
        loading={agreement.loading}
        locale={{ emptyText: t('common.no-data-found') }}
        pagination={false}
      />
    </>
  )
}
